/**
 * Cache Data Validation Schemas
 * 
 * Zod schemas for validating cached data before storage.
 * Prevents corrupted or invalid data from polluting the cache.
 */

import { z } from 'zod';

// Base item schema
export const ItemSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  icon: z.string().optional(),
  description: z.string().optional(),
  type: z.string().optional(),
  rarity: z.string().optional(),
  value: z.number().optional(),
  stackable: z.boolean().optional(),
  maxStack: z.number().positive().optional(),
});

// Inventory item schema
export const InventoryItemSchema = z.object({
  itemId: z.string().min(1),
  quantity: z.number().int().min(0),
  definition: ItemSchema.optional(),
});

// Player stats schema
export const PlayerStatsSchema = z.object({
  pieces: z.number().int().min(0),
  level: z.number().int().min(1),
  xp: z.number().int().min(0),
  infamy: z.number().int().min(0),
  hullHp: z.number().min(0),
  maxHullHp: z.number().positive(),
  sailHp: z.number().min(0),
  maxSailHp: z.number().positive(),
  sp: z.number().min(0),
  maxSp: z.number().positive(),
  x: z.number().optional(),
  y: z.number().optional(),
});

// Skill data schema
export const SkillDataSchema = z.object({
  id: z.string().min(1),
  level: z.number().int().min(0),
  xp: z.number().int().min(0),
  maxLevel: z.number().int().positive(),
});

// Equipment slot schema
export const EquipmentSlotSchema = z.object({
  slot: z.string().min(1),
  itemId: z.string().min(1),
});

// Hotbar slot schema
export const HotbarSlotSchema = z.record(z.number().int().min(1).max(10), z.string());

// Island data schema
export const IslandDataSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1).optional(),
  x: z.number(),
  y: z.number(),
  size: z.number().positive().optional(),
  radius: z.number().optional(),
  type: z.string().optional(),
  owner: z.string().optional(),
  buildings: z.array(z.any()).optional(),
  resources: z.array(z.any()).optional(),
  tradingPost: z.boolean().optional(),
  permanentName: z.string().nullable().optional(),
  shape: z.array(z.any()).optional(),
  claimRadius: z.number().nullable().optional(),
  isPermanent: z.boolean().optional(),
  biome: z.string().optional(),
  ownerId: z.string().nullable().optional(),
  ownerType: z.string().nullable().optional(),
  ownerName: z.string().nullable().optional(),
  ownerShipColor: z.string().nullable().optional(),
  customName: z.string().nullable().optional(),
});

// Fishing spot data schema
export const FishingSpotDataSchema = z.object({
  id: z.string().min(1),
  x: z.number().optional(),
  y: z.number().optional(),
  tileX: z.number().optional(),
  tileY: z.number().optional(),
  type: z.string().optional(),
  rarity: z.string().optional(),
  fishRemaining: z.number().int().min(0).optional(),
  maxFish: z.number().int().positive().optional(),
  respawnTime: z.number().int().min(0).optional(),
});

// Chunk data schema
export const ChunkDataSchema = z.object({
  islands: z.array(IslandDataSchema).optional(),
  fishingSpots: z.array(FishingSpotDataSchema).optional(),
});

// Player data schema
export const PlayerDataSchema = z.object({
  inventory: z.array(InventoryItemSchema).optional(),
  skills: z.record(z.string(), SkillDataSchema).optional(),
  stats: PlayerStatsSchema.optional(),
  equipment: z.record(z.string(), z.string()).optional(),
  hotbar: HotbarSlotSchema.optional(),
});

// Market data schema
export const MarketDataSchema = z.object({
  shopItems: z.array(z.object({
    id: z.string().min(1),
    price: z.number().positive(),
    stock: z.number().int().min(0),
    item: ItemSchema.optional(),
  })).optional(),
  prices: z.record(z.string(), z.number().positive()).optional(),
  lastUpdated: z.number().optional(),
});

// UI state schema (flexible)
export const UIStateSchema = z.any().refine(
  (val) => val !== null && val !== undefined,
  { message: 'UI state cannot be null or undefined' }
);

// Cache entry wrapper schema
export const CacheEntrySchema = z.object({
  data: z.unknown(),
  timestamp: z.number().int().positive(),
  ttl: z.number().int().positive().optional(),
  accessCount: z.number().int().min(0),
  lastAccessed: z.number().int().positive(),
  version: z.number().int().min(0).optional(),
  serverTimestamp: z.number().int().positive().optional(),
  etag: z.string().optional(),
});

// Export type inference
export type Item = z.infer<typeof ItemSchema>;
export type InventoryItem = z.infer<typeof InventoryItemSchema>;
export type PlayerStats = z.infer<typeof PlayerStatsSchema>;
export type SkillData = z.infer<typeof SkillDataSchema>;
export type EquipmentSlot = z.infer<typeof EquipmentSlotSchema>;
export type HotbarSlot = z.infer<typeof HotbarSlotSchema>;
export type IslandData = z.infer<typeof IslandDataSchema>;
export type FishingSpotData = z.infer<typeof FishingSpotDataSchema>;
export type ChunkData = z.infer<typeof ChunkDataSchema>;
export type PlayerData = z.infer<typeof PlayerDataSchema>;
export type MarketData = z.infer<typeof MarketDataSchema>;
export type UIState = z.infer<typeof UIStateSchema>;
export type CacheEntry = z.infer<typeof CacheEntrySchema>;

/**
 * Data validator class
 */
export class DataValidator {
  /**
   * Validate chunk data
   */
  static validateChunkData(data: unknown): ChunkData {
    return ChunkDataSchema.parse(data);
  }

  /**
   * Validate player data
   */
  static validatePlayerData(data: unknown): PlayerData {
    return PlayerDataSchema.parse(data);
  }

  /**
   * Validate market data
   */
  static validateMarketData(data: unknown): MarketData {
    return MarketDataSchema.parse(data);
  }

  /**
   * Validate UI state
   */
  static validateUIState(data: unknown): UIState {
    return UIStateSchema.parse(data);
  }

  /**
   * Safe validation with fallback
   */
  static safeValidate<T>(
    schema: z.ZodSchema<T>,
    data: unknown,
    fallback?: T
  ): { success: true; data: T } | { success: false; error: string; data?: T } {
    try {
      const validated = schema.parse(data);
      return { success: true, data: validated };
    } catch (error) {
      if (error instanceof z.ZodError) {
        const message = error.issues.map((e: any) => `${e.path.join('.')}: ${e.message}`).join(', ');
        console.error('Validation error:', message);
        return { success: false, error: message, data: fallback };
      }
      console.error('Unexpected validation error:', error);
      return { success: false, error: 'Unknown validation error', data: fallback };
    }
  }

  /**
   * Create a validation middleware for cache operations
   */
  static createValidationMiddleware<T>(
    schema: z.ZodSchema<T>,
    options: {
      strict?: boolean; // Throw on validation errors
      fallback?: T; // Return fallback on validation errors
      logErrors?: boolean; // Log validation errors
    } = {}
  ) {
    const { strict = false, fallback, logErrors = true } = options;

    return (data: unknown): T | null => {
      const result = this.safeValidate(schema, data, fallback);
      
      if (!result.success) {
        if (logErrors) {
          console.error('Cache validation failed:', result.error);
        }
        
        if (strict) {
          throw new Error(`Validation failed: ${result.error}`);
        }
        
        return result.data || null;
      }
      
      return result.data;
    };
  }
}

export default DataValidator;
